import { Table, TableBody, TableCell, TableFooter, TableHead, TablePagination, TableRow } from "@mui/material";
import React, { useState, useCallback, useEffect, useRef, useMemo, useContext } from "react";
import { Link } from "react-router-dom";
import {
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ScrollArea,
  Textarea,
  useToast
} from "randmarcomps";
import ButtonWithActions from "../../components/util/ButtonWithActions";
import ManufacturerContext from "../../contexts/ManufacturerContext";
import Path from "../../routes/Path";
import Return from "../../stores/return/Return";
import SingleReturnActions from "../returns/SingleReturnActions";
import { UserCircle2, SendHorizontal, Loader2 } from "lucide-react";
import {
  Return as NewReturn,
  usePostV4ResellerByRouteResellerIdReturnAndReturnNumberAddCommentMutation,
  useGetV4WarehousesQuery
} from "randmar-api-client";

type ParsedComment = {
  message: string;
  meta: string;
};

interface Props {
  returns?: (Return | NewReturn)[];
  defaultRowsPerPage?: number;
}

const isLegacyReturn = (ret: Return | NewReturn): ret is Return => 'returnNumber' in ret;

const convertLegacyReturnToApiReturn = (legacyReturn: Return): NewReturn => {
  const { returnNumber, resellerId, status, invoiceNumber, requestDate, creditNumber, contactName, contactPhone, contactEmail, comments, mpn, quantity, resellerName, warehouseLocation } = legacyReturn;
  return {
    ReturnNumber: returnNumber,
    ResellerId: resellerId ? String(resellerId) : undefined,
    Status: status,
    InvoiceNumber: invoiceNumber,
    RequestDate: requestDate,
    CreditNumber: creditNumber,
    ContactName: contactName,
    ContactPhone: contactPhone,
    ContactEmail: contactEmail,
    Comments: comments,
    RandmarSKU: mpn,
    Quantity: quantity,
    ResellerName: resellerName,
    WarehouseLocation: warehouseLocation,
  };
};

const convertApiReturnToLegacyReturn = (apiReturn: NewReturn): Return => {
  const legacyReturn = new Return();
  Object.assign(legacyReturn, {
    returnNumber: apiReturn.ReturnNumber,
    resellerId: apiReturn.ResellerId ? parseInt(apiReturn.ResellerId, 10) : undefined,
    status: apiReturn.Status,
    invoiceNumber: apiReturn.InvoiceNumber,
    requestDate: apiReturn.RequestDate,
    creditNumber: apiReturn.CreditNumber,
    contactName: apiReturn.ContactName,
    contactPhone: apiReturn.ContactPhone,
    contactEmail: apiReturn.ContactEmail,
    comments: apiReturn.Comments,
    mpn: apiReturn.RandmarSKU,
    quantity: apiReturn.Quantity,
    resellerName: apiReturn.ResellerName,
    warehouseLocation: apiReturn.WarehouseLocation,
  });
  return legacyReturn;
};

const ReturnGrid: React.FC<Props> = ({ returns, defaultRowsPerPage }) => {
  const { applicationId } = useContext(ManufacturerContext);
  const [localReturns, setLocalReturns] = useState<NewReturn[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage || 20);
  const [isCommentsDialogOpen, setCommentsDialogOpen] = useState(false);
  const [selectedComments, setSelectedComments] = useState<ParsedComment[]>([]);
  const [selectedReturn, setSelectedReturn] = useState<NewReturn | null>(null);
  const [newComment, setNewComment] = useState("");
  const { toast } = useToast();
  const lastCommentRef = useRef<HTMLDivElement>(null);

  const { data: warehouses } = useGetV4WarehousesQuery();

  const [addComment, { isLoading: isAddingComment }] = usePostV4ResellerByRouteResellerIdReturnAndReturnNumberAddCommentMutation();

  useEffect(() => {
    if (!returns) {
      setLocalReturns([]);
      return;
    }

    const newReturns = returns.map(r => isLegacyReturn(r) ? convertLegacyReturnToApiReturn(r) : r);
    setLocalReturns(newReturns);
  }, [returns]);

  useEffect(() => {
    if (isCommentsDialogOpen) {
      lastCommentRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [selectedComments, isCommentsDialogOpen]);

  const sortedReturns = useMemo(() => {
    return [...localReturns].sort((a, b) => (b.ReturnNumber || "").localeCompare(a.ReturnNumber || ""));
  }, [localReturns]);

  const handleChangePage = useCallback((_: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const parseComments = useCallback((comments: string | undefined | null): ParsedComment[] => {
    if (!comments) {
      return [];
    }
    const lines = comments.split(/\r\n/).filter(line => line.trim() !== '');
    return lines.map(line => {
      const delimiter = " by ";
      const delimiterIndex = line.indexOf(delimiter);
      if (delimiterIndex === -1) {
        return { message: line.trim(), meta: "" };
      }
      const message = line.substring(0, delimiterIndex).trim();
      const meta = line.substring(delimiterIndex).trim();
      return { message, meta };
    });
  }, []);

  const handleOpenCommentsDialog = useCallback((returnDoc: NewReturn) => {
    setSelectedReturn(returnDoc);
    setSelectedComments(parseComments(returnDoc.Comments));
    setCommentsDialogOpen(true);
  }, [parseComments]);

  const handleCloseCommentsDialog = useCallback(() => {
    setCommentsDialogOpen(false);
    setSelectedReturn(null);
    setNewComment("");
  }, []);

  const getWarehouseNameFromCode = useCallback((code: string | undefined): string => {
    if (!code || !warehouses) {
      return "B/O";
    }
    const warehouse = warehouses.find(w => w.WarehouseId === code);
    return warehouse?.Name || "B/O";
  }, [warehouses]);

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedReturn) return;

    try {
      await addComment({
        routeResellerId: selectedReturn.ResellerId ?? "",
        returnNumber: selectedReturn.ReturnNumber ?? "",
        comment: newComment.trim()
      }).unwrap();
      
      const newCommentText = `${newComment.trim()} by You just now`;
      const updatedComments = selectedReturn.Comments
        ? `${selectedReturn.Comments}\r\n${newCommentText}`
        : newCommentText;

      const updatedReturn = {
        ...selectedReturn,
        Comments: updatedComments
      };

      setLocalReturns(prevReturns => {
        const returnIndex = prevReturns.findIndex(r => r.ReturnNumber === selectedReturn.ReturnNumber);
        if (returnIndex !== -1) {
          const newReturns = [...prevReturns];
          newReturns[returnIndex] = updatedReturn;
          return newReturns;
        }
        return prevReturns;
      });

      setSelectedReturn(updatedReturn);
      setSelectedComments(parseComments(updatedReturn.Comments));
      setNewComment("");
      toast({ variant: "success", title: "Success", description: "Comment added successfully." });
    } catch (error) {
      console.error("Failed to add comment:", error);
      toast({ variant: "destructive", title: "Error", description: "Failed to add comment. Please try again." });
    }
  };


  if (!localReturns?.length) {
    return (
      <div className="grid place-items-center text-center">
        <p>There are no returns!</p>
      </div>
    );
  }

  return (
    <>
      <Card className="w-full">
        <div className="w-full overflow-auto rounded-[20px]">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell align="center" sx={{ borderColor: "#582c83" }}>Reseller Name</TableCell>
                <TableCell align="center" sx={{ borderColor: "#582c83" }}>Return Number</TableCell>
                <TableCell align="center" sx={{ borderColor: "#582c83" }}>Part Number</TableCell>
                <TableCell align="center" sx={{ borderColor: "#582c83" }}>Quantity</TableCell>
                <TableCell align="center" sx={{ borderColor: "#582c83" }}>Comments</TableCell>
                <TableCell align="center" sx={{ borderColor: "#582c83" }}>Location</TableCell>
                <TableCell align="center" sx={{ borderColor: "#582c83" }}>Request Date</TableCell>
                <TableCell align="center" sx={{ borderColor: "#582c83" }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedReturns
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row) => {
                  const commentsArray = parseComments(row.Comments);
                  const legacyRow = convertApiReturnToLegacyReturn(row);
                  return (
                    <TableRow sx={{ "&:hover": { backgroundColor: "#f9f5ff" } }} key={"Return" + row.ReturnNumber}>
                      <TableCell className={"notranslate"} align="center"> <Link to={Path.reseller(applicationId, row.ResellerId || "")}>{row.ResellerName}</Link></TableCell>
                      <TableCell align="center">{row.ReturnNumber}</TableCell>
                      <TableCell align="center">
                        <Link to={Path.product(applicationId, row.RandmarSKU ?? undefined)}>
                          {row.RandmarSKU}
                        </Link>
                      </TableCell>
                      <TableCell align="center">{row.Quantity}</TableCell>
                      <TableCell align="center">
                        {commentsArray.length > 0 ? (
                          <Button variant="outline" size="sm" onClick={() => handleOpenCommentsDialog(row)}>
                            {commentsArray.length} {commentsArray.length === 1 ? "Comment" : "Comments"}
                          </Button>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell align="center">{getWarehouseNameFromCode(row.WarehouseLocation ?? undefined)}</TableCell>
                      <TableCell align="center">{(row.RequestDate || "").split('T')[0]}</TableCell>
                      <TableCell align="center">
                        <ButtonWithActions>
                          <SingleReturnActions returnDocument={legacyRow} />
                        </ButtonWithActions>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  colSpan={8}
                  count={sortedReturns.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  sx={{ '& .MuiTablePagination-spacer': { display: 'none' }, '& .MuiTablePagination-toolbar': { justifyContent: 'flex-start' } }}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </div>
      </Card>

      <Dialog open={isCommentsDialogOpen} onOpenChange={(isOpen) => !isOpen && handleCloseCommentsDialog()}>
        <DialogContent className="sm:max-w-[625px]">
          <DialogHeader>
            <DialogTitle>Return Comments for #{selectedReturn?.ReturnNumber}</DialogTitle>
            <DialogDescription>
              History of all comments for this return.
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="h-[350px] w-full rounded-md border p-4">
            {selectedComments.length > 0 ? selectedComments.map((comment, index) => (
              <div 
                key={index}
                ref={index === selectedComments.length - 1 ? lastCommentRef : null}
                className="flex items-start space-x-3 text-sm p-2 border-b last:border-b-0">
                <UserCircle2 className="h-4 w-4 mt-1 text-gray-500 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-gray-800">{comment.message}</p>
                  <p className="text-gray-500 text-xs">{comment.meta}</p>
                </div>
              </div>
            )) : <p className="text-center text-gray-500">No comments yet.</p>}
          </ScrollArea>
          <div className="mt-4 space-y-3">
            <div className="flex items-start space-x-2">
              <Textarea
                placeholder="Add comment..."
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                className="min-h-[60px] flex-1"
                disabled={isAddingComment}
              ></Textarea>
              <Button
                type="button"
                size="icon"
                onClick={handleAddComment}
                disabled={!newComment.trim() || isAddingComment}
              >
                {isAddingComment ? 
                  <Loader2 className="h-4 w-4 animate-spin" /> : 
                  <SendHorizontal className="h-4 w-4" />
                }
              </Button>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button type="button" variant="outline" onClick={handleCloseCommentsDialog}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ReturnGrid;
